import React, { useEffect, useRef, useState } from "react";
import { makeStyles, useTheme, Popper, Dropdown, MenuIcon } from "@seahorse/ui";

interface CoHedgerPopOverMenuProps {
  // applied only on Submit
  setCurrencyPair: (currency: string) => void;
  setAsset_type: (assetType: string) => void;
  setExecutionType: (executionType: string) => void;
  setExpirationDate: (date: any | null) => void;

  // optional: if you want to prefill current values
  currencyPair?: string;
  assetType?: string;
  executionType?: string;
  expirationDate?: string | null; // ✅ NEW
}

const currencyList = [
  "USDJPY",
  "EURUSD",
  "GBPUSD",
  "USDCHF",
  "USDCAD",
  "AUDUSD",
  "NZDUSD",
  "USDNOK",
  "USDSEK",
  "USDINR",
];

const assetTypeList = ["STOCK", "BOND"] as const;
const executionTypeList = ["SELL", "BUY"] as const;

const CoHedgerPopOverMenu: React.FunctionComponent<CoHedgerPopOverMenuProps> = ({
  setCurrencyPair,
  setAsset_type,
  setExecutionType,
  setExpirationDate,
  currencyPair = "USDJPY",
  assetType = "STOCK",
  executionType = "SELL",
  expirationDate = null, // ✅ NEW
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const menuIconRef: any = useRef(null);
  const popperRef: any = useRef(null);

  const theme = useTheme();
  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      padding: "10px",
      zIndex: 1000,
      backgroundColor: theme.globallink.colors.DarkGrey[500],
    },
    menuIcon: {
      cursor: "pointer",
      color: theme.globallink.colors.Slate[700],
    },
    popperContainer: {
      padding: "16px",
      backgroundColor: theme.globallink.colors.Slate[900],
      borderRadius: "8px",
      boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.35)",
      maxWidth: "320px",
      zIndex: 1000,
      width: "320px",
      color: "#E0E0E0",
    },
    dropdownContainer: { marginBottom: "12px" },
    title: {
      color: "#E0E0E0",
      fontSize: "14px",
      marginBottom: "6px",
      fontWeight: "bold",
      opacity: 0.9,
    },

    // ✅ NEW: Date input styles
    dateInput: {
      width: "100%",
      height: "40px",
      borderRadius: "10px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      padding: "0 12px",
      outline: "none",
      fontSize: "13px",
      WebkitAppearance: "none",
      appearance: "none",
    },
    hint: {
      marginTop: "6px",
      fontSize: "11px",
      color: "rgba(224,224,224,0.7)",
    },

    buttonRow: {
      display: "flex",
      gap: 10,
      justifyContent: "flex-end",
      marginTop: 12,
    },
    button: {
      cursor: "pointer",
      padding: "10px 12px",
      borderRadius: "10px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(80, 170, 255, 0.16)",
      color: "#E0E0E0",
      fontWeight: 700,
    },
    secondaryButton: {
      cursor: "pointer",
      padding: "10px 12px",
      borderRadius: "10px",
      border: "1px solid rgba(255,255,255,0.14)",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      fontWeight: 600,
    },
  });

  const classes = useStyles();

  // dropdown items
  const currencyItems = [...currencyList]
    .sort((a, b) => a.localeCompare(b))
    .map((currency) => ({ label: currency, value: currency }));
  const assetItems = assetTypeList.map((a) => ({ label: a, value: a }));
  const execItems = executionTypeList.map((e) => ({ label: e, value: e }));

  // ✅ local draft state (changes immediately)
  const [draftCurrency, setDraftCurrency] = useState<string>(currencyPair);
  const [draftAsset, setDraftAsset] = useState<any>(assetType);
  const [draftExec, setDraftExec] = useState<any>(executionType);
  const [draftExpiration, setDraftExpiration] = useState<string | null>(
    expirationDate
  ); // ✅ NEW

  // if parent values change, update drafts
  useEffect(() => setDraftCurrency(currencyPair), [currencyPair]);
  useEffect(() => setDraftAsset(assetType), [assetType]);
  useEffect(() => setDraftExec(executionType), [executionType]);
  useEffect(() => setDraftExpiration(expirationDate), [expirationDate]); // ✅ NEW

  // close on outside click
  useEffect(() => {
    const handleOutsideClick = (event: Event) => {
      setTimeout(() => {
        if (
          popperRef.current &&
          !popperRef.current.contains(event.target) &&
          menuIconRef.current &&
          !menuIconRef.current.contains(event.target)
        ) {
          setIsModalOpen(false);
        }
      }, 200);
    };

    document.addEventListener("mousedown", handleOutsideClick, true);
    return () =>
      document.removeEventListener("mousedown", handleOutsideClick, true);
  }, []);

  // ✅ only apply changes on submit
  const handleSubmit = () => {
    setCurrencyPair(draftCurrency);
    setAsset_type(draftAsset);
    setExecutionType(draftExec);
    setExpirationDate(draftExpiration); // ✅ NEW
    setIsModalOpen(false);
  };

  // reset drafts back to current applied values
  const handleReset = () => {
    setDraftCurrency(currencyPair);
    setDraftAsset(assetType);
    setDraftExec(executionType);
    setDraftExpiration(expirationDate); // ✅ NEW
  };

  return (
    <div className={classes.container}>
      <div
        ref={menuIconRef}
        className={classes.menuIcon}
        onClick={() => setIsModalOpen((prev) => !prev)}
      >
        <MenuIcon />
      </div>

      <Popper
        open={isModalOpen}
        anchorEl={menuIconRef.current}
        placement="bottom-start"
        ref={popperRef}
      >
        <div className={classes.popperContainer}>
          <div className={classes.title}>Currency Pair</div>
          <div className={classes.dropdownContainer}>
            <Dropdown
              items={currencyItems}
              value={draftCurrency}
              onChange={(v: any) => setDraftCurrency(v)}
              allowBlankSelection={false}
              placeholder="Select currency"
              fullWidth={true}
            />
          </div>

          <div className={classes.title}>Asset Type</div>
          <div className={classes.dropdownContainer}>
            <Dropdown
              items={assetItems}
              value={draftAsset}
              onChange={(v: any) => setDraftAsset(v)}
              allowBlankSelection={false}
              placeholder="Select asset type"
              fullWidth={true}
            />
          </div>

          <div className={classes.title}>Execution Type</div>
          <div className={classes.dropdownContainer}>
            <Dropdown
              items={execItems}
              value={draftExec}
              onChange={(v: any) => setDraftExec(v)}
              allowBlankSelection={false}
              placeholder="Select execution type"
              fullWidth={true}
            />
          </div>

          {/* ✅ NEW: Expiration Date */}
          <div className={classes.title}>Expiration Date (optional)</div>
          <div className={classes.dropdownContainer}>
            <input
              className={classes.dateInput as any}
              type="date"
              value={draftExpiration || ""}
              onChange={(e) =>
                setDraftExpiration(e.target.value ? e.target.value : null)
              }
            />
            <div className={classes.hint}>
              Leave blank if you don’t have a specific expiry.
            </div>
          </div>

          <div className={classes.buttonRow}>
            <button
              className={classes.secondaryButton}
              onClick={handleReset}
              type="button"
            >
              Reset
            </button>
            <button className={classes.button} onClick={handleSubmit} type="button">
              Submit
            </button>
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default CoHedgerPopOverMenu;
